/**
 * BrandLogo — brand-faithful vendor logo for any slug we have a registered
 * brand mapping for (LLM providers, AI clients, common SaaS vendors). Renders
 * the real wordmark/logo when the slug resolves; falls back to <AppIcon /> for
 * unknown slugs.
 *
 * This is the model-side companion to <ConnectorLogo />. The split is:
 *
 *   - <ConnectorLogo /> — consumes `logo_url` straight from the DB row
 *     (`mcp__connector_template.logo_url`). DB is source of truth. Good for
 *     connectors because the catalog row carries the URL.
 *
 *   - <BrandLogo /> — slug-keyed registry; no DB column needed. Good for
 *     LLM providers, AI clients (Claude / ChatGPT / Cursor), and any
 *     surface that has a slug but no logo_url column. Uniform brand
 *     coverage with zero schema change.
 *
 * Logos are sourced from simpleicons.org's free CDN — public-domain (CC0)
 * SVG brand marks usable as identifiers (showing "this connects to X"),
 * which is the canonical fair use here. Slugs that aren't on simpleicons
 * (notably: openai, chatgpt, amazon_web_services as of late 2026) are
 * served via inline SVG so we don't ship 404s.
 *
 * Adding a new brand: extend BRAND_REGISTRY below with a (slug → url|inline)
 * entry. Slugs match the DB column they come from
 * (`llm__provider_template.name`, `mcp__connector_template.name`).
 */

import { useState } from 'react';
import type { ReactNode } from 'react';

import { AppIcon } from './AppIcon.tsx';

/** Brand source: either a remote URL (preferred — simpleicons CDN) or an
 *  inline SVG (used when simpleicons doesn't host the brand). */
type BrandSource = { kind: 'url'; url: string } | { kind: 'inline'; svg: ReactNode };

/** simpleicons.org CDN URL pattern: cdn.simpleicons.org/{slug}/{hex_color}.
 *  Without hex → default monochrome black. With hex → tinted to brand color. */
function simpleicons(slug: string, hex: string): BrandSource {
  return { kind: 'url', url: `https://cdn.simpleicons.org/${slug}/${hex}` };
}

/** Inline SVG escape hatch for brands not on simpleicons. Wraps the path in
 *  a 24x24 viewBox. The path uses brand color directly. */
function inlineSvg(path: string, color: string): BrandSource {
  return {
    kind: 'inline',
    svg: (
      <svg viewBox="0 0 24 24" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
        <path d={path} fill={color} />
      </svg>
    ),
  };
}

/** Multi-path inline SVG for multi-color brands (e.g. Google's G). */
function inlineSvgPaths(paths: Array<{ d: string; fill: string }>): BrandSource {
  return {
    kind: 'inline',
    svg: (
      <svg viewBox="0 0 24 24" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
        {paths.map((p, i) => (
          <path key={i} d={p.d} fill={p.fill} />
        ))}
      </svg>
    ),
  };
}

/**
 * Brand registry — slug → source. Slugs match the DB columns they come from:
 *
 *   LLM providers (llm__provider_template.name):
 *     anthropic, openai, google, aws_bedrock
 *
 *   AI clients (Home "use Olytic in your favorite app" tiles):
 *     claude, chatgpt, cursor
 *
 *   Common SaaS vendors that also appear as slugs in other surfaces
 *   (request rows in AdminRequests, search results in GlobalSearch where
 *    the vendor_slug is supplied but no logo_url column was joined):
 *     gmail, salesforce, slack, hubspot, notion, supabase, ga4,
 *     google_ads, google_drive, linkedin_ads, stripe, etc.
 *
 * For connectors where the DB row carries logo_url, prefer <ConnectorLogo />
 * (it consumes the DB value directly). BrandLogo is the bridge for surfaces
 * that don't have logo_url at hand.
 */
const BRAND_REGISTRY: Record<string, BrandSource> = {
  // ---- LLM providers ----------------------------------------------------
  anthropic: simpleicons('anthropic', '181818'),
  // OpenAI: not on simpleicons CDN; inline using the official "knot" mark.
  openai: inlineSvg(
    'M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z',
    '#412991',
  ),
  // Google: brand has a multi-color "G". Inline so we keep the 4-color
  // identity instead of monochrome.
  google: inlineSvgPaths([
    { d: 'M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z', fill: '#4285F4' },
    { d: 'M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z', fill: '#34A853' },
    { d: 'M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z', fill: '#FBBC05' },
    { d: 'M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z', fill: '#EA4335' },
  ]),
  // AWS Bedrock — use AWS slug; simpleicons hosts under "amazonaws" alias
  // but the CDN slug requires a specific value. Inline the AWS "smile" mark.
  aws_bedrock: inlineSvg(
    'M6.76 14.93c0 .35.04.63.11.84.07.21.18.43.31.66.04.07.07.14.07.21 0 .1-.06.21-.2.31l-.66.43c-.1.07-.2.1-.27.1-.1 0-.21-.06-.31-.16-.14-.15-.27-.32-.38-.49-.11-.18-.22-.39-.34-.65-.83.97-1.86 1.46-3.11 1.46-.88 0-1.59-.25-2.11-.76-.52-.51-.79-1.19-.79-2.04 0-.9.32-1.63.97-2.18.65-.55 1.51-.83 2.61-.83.36 0 .73.03 1.12.09.39.06.79.15 1.21.26v-.78c0-.81-.17-1.38-.51-1.71-.34-.33-.92-.49-1.74-.49-.37 0-.76.04-1.15.13-.39.09-.78.21-1.15.36-.17.07-.3.12-.37.14-.07.02-.13.03-.17.03-.15 0-.22-.1-.22-.31v-.51c0-.16.02-.28.07-.36.05-.07.14-.14.27-.21.37-.19.81-.35 1.33-.48.52-.13 1.07-.19 1.65-.19 1.26 0 2.18.29 2.78.86.6.57.9 1.45.9 2.62v3.45zm-4.3 1.61c.35 0 .71-.06 1.09-.19.38-.13.72-.36 1.01-.69.17-.21.3-.43.36-.69.07-.26.11-.57.11-.93v-.45a8.7 8.7 0 0 0-1.97-.27c-.7 0-1.21.14-1.55.42-.34.28-.51.68-.51 1.21 0 .49.13.86.39 1.11.27.27.65.4 1.07.4zm8.5 1.15c-.18 0-.3-.03-.38-.1-.08-.06-.15-.2-.21-.39l-2.36-7.78c-.06-.2-.09-.33-.09-.4 0-.16.08-.25.23-.25h1.03c.19 0 .32.03.39.1.08.06.14.2.2.39l1.69 6.66 1.57-6.66c.05-.2.11-.33.19-.39.08-.06.22-.1.4-.1h.84c.19 0 .32.03.4.1.08.06.15.2.19.39l1.59 6.74 1.74-6.74c.06-.2.13-.33.2-.39.08-.06.21-.1.39-.1h.98c.15 0 .24.08.24.25 0 .05-.01.1-.02.16-.01.06-.03.14-.07.25l-2.42 7.78c-.06.2-.13.33-.21.39-.08.06-.21.1-.38.1h-.9c-.19 0-.32-.03-.4-.1-.08-.07-.15-.2-.19-.4l-1.56-6.49-1.55 6.48c-.05.2-.11.33-.19.4-.08.07-.22.1-.4.1h-.91zm13.61.28c-.55 0-1.1-.06-1.62-.19-.52-.13-.93-.27-1.2-.43-.17-.1-.28-.2-.32-.3a.755.755 0 0 1-.07-.3v-.53c0-.22.08-.32.23-.32.06 0 .12.01.18.03.06.02.15.06.25.1.34.15.71.27 1.1.35.39.08.78.12 1.18.12.62 0 1.1-.11 1.44-.33.34-.22.51-.54.51-.95 0-.28-.09-.51-.27-.7-.18-.19-.52-.36-1.02-.52l-1.46-.46c-.74-.24-1.28-.58-1.61-1.04-.33-.45-.5-.95-.5-1.49 0-.43.09-.81.27-1.14.18-.33.43-.62.74-.85.31-.24.66-.42 1.07-.55.41-.13.84-.18 1.29-.18.23 0 .46.01.69.04.23.03.45.07.66.12.21.06.4.12.59.19.18.07.32.14.42.21.14.08.24.16.3.25.06.08.09.19.09.32v.49c0 .22-.08.34-.23.34-.08 0-.21-.04-.38-.12a4.74 4.74 0 0 0-1.92-.39c-.56 0-1 .09-1.31.28-.31.18-.46.47-.46.86 0 .27.1.5.29.69.19.19.55.38 1.09.55l1.43.46c.73.23 1.25.55 1.56.96.31.41.46.88.46 1.4 0 .44-.09.83-.27 1.18-.18.34-.43.65-.75.89-.32.25-.7.43-1.14.56-.46.14-.94.21-1.46.21z',
    '#FF9900',
  ),
  // Convenience aliases — some surfaces use shorter slugs for the same provider.
  bedrock: simpleicons('amazonaws', 'FF9900'),
  aws: simpleicons('amazonaws', 'FF9900'),

  // ---- AI clients (Home "use Olytic in your favorite AI app") -----------
  claude: simpleicons('claude', 'D97757'),
  // ChatGPT: not on simpleicons CDN under that slug. Inline the official
  // OpenAI mark in green to read as "ChatGPT" (same shape as openai, OpenAI's
  // green is the ChatGPT brand color).
  chatgpt: inlineSvg(
    'M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z',
    '#10A37F',
  ),
  cursor: simpleicons('cursor', '1A1A1A'),

  // ---- Common SaaS vendors (cross-reference; primary path is ConnectorLogo) ----
  gmail: simpleicons('gmail', 'EA4335'),
  salesforce: simpleicons('salesforce', '00A1E0'),
  slack: simpleicons('slack', '4A154B'),
  hubspot: simpleicons('hubspot', 'FF7A59'),
  notion: simpleicons('notion', '000000'),
  supabase: simpleicons('supabase', '3FCF8E'),
  ga4: simpleicons('googleanalytics', 'E37400'),
  google_analytics: simpleicons('googleanalytics', 'E37400'),
  google_ads: simpleicons('googleads', '4285F4'),
  google_drive: simpleicons('googledrive', '4285F4'),
  google_sheets: simpleicons('googlesheets', '34A853'),
  linkedin_ads: simpleicons('linkedin', '0A66C2'),
  linkedin: simpleicons('linkedin', '0A66C2'),
  stripe: simpleicons('stripe', '635BFF'),
  snowflake: simpleicons('snowflake', '29B5E8'),
  meta_ads: simpleicons('meta', '0467DF'),
  apollo: simpleicons('apollo', '1D4ED8'),
  asana: simpleicons('asana', 'F06A6A'),
  linear: simpleicons('linear', '5E6AD2'),
  cloudflare: simpleicons('cloudflare', 'F38020'),
  quickbooks: simpleicons('quickbooks', '2CA01C'),
};

interface BrandLogoProps {
  /** Vendor / provider slug. Drives both the brand lookup AND the
   *  initial-tile fallback when no brand is registered. */
  slug: string;
  /** Display name. Used only for the initial-tile fallback alt text + glyph. */
  name: string;
  size?: 'sm' | 'md' | 'lg';
}

export function BrandLogo({ slug, name, size = 'md' }: BrandLogoProps) {
  const [errored, setErrored] = useState(false);
  const source = BRAND_REGISTRY[slug];

  // Unknown slug, or remote-CDN load failed at runtime → fall back to the
  // initial tile. AppIcon picks the brand color when slug is in APP_COLORS,
  // otherwise warm grey.
  if (source === undefined || errored) {
    return <AppIcon slug={slug} name={name} size={size} />;
  }

  const dim = size === 'lg' ? 56 : size === 'sm' ? 28 : 40;
  const radius = size === 'sm' ? 8 : 10;
  const innerPad = size === 'lg' ? 10 : size === 'sm' ? 5 : 7;

  return (
    <div
      style={{
        width: `${dim}px`,
        height: `${dim}px`,
        borderRadius: `${radius}px`,
        background: 'var(--olytic-white)',
        border: '1px solid var(--border)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        flexShrink: 0,
        padding: `${innerPad}px`,
        boxSizing: 'border-box',
      }}
      role="img"
      aria-label={`${name} logo`}
    >
      {source.kind === 'url' ? (
        <img
          src={source.url}
          alt=""
          loading="lazy"
          onError={() => setErrored(true)}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
        />
      ) : (
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {source.svg}
        </div>
      )}
    </div>
  );
}
